import { Dispatch, Fragment } from 'react';
import { Appointment, Patient as AppointmentPatient } from '../api/fetchAppointmentsV2';
import { GenieSettings } from '../api/pushGenie';
import { AntTooltip } from '../components/common/AntTooltip';
import { GO_TO_ADD_NEW_PATIENT, GO_TO_APPOINTMENTS, GO_TO_PRESCRIPTION_PAD } from '../routes/paths';
import { NewPatient, PPA, WithBEPatient, triggerStartVisitOnBE } from '../store/actions/app';
import { initVisit } from '../store/actions/visit';
import {
    MISC_KEYS,
    getDoc,
    getItemFromMisc,
    getMe,
    getPatientByLocalId,
    getVisitById,
} from '../store/idbHandler';
import { Age, Patient, getPtFullName } from '../store/types/patient';
import { Visit } from '../store/types/visit';
import { calculateAge } from '../store/utils';
import { track } from '../tracking';
import { AllPatientsComponent } from './AllPatients';
import { AP, APWithOptionalpatient, Appointments } from './Appointments';
import { NewPatient as NewPatientComponent, NewPatientLocationState } from './NewPatient';
import { NewQueue } from './NewQueue';
import { PatientDetailsComponent } from './PatientDetails';
import bookAppointment, { RequestResponse } from '../api/bookAppointment';
import { his } from './history';
import { captureError, captureErrorString } from './captureError';
import { getStore } from '../App';
import { REFRESH_APPT } from '../bridge/events/types';
import { StartVisitBtn } from '../components/globalsearch/StartVisitBtn';
import { dispatch as ekaDispatch } from '../bridge/events/dispatch';
import { setDontAllowStartVisit, setIsNotesMode } from '../store/actions/session';
import { PastVisitsRightPanel } from './PastVisitsRightPanel';
import { MyDoc } from '../api/fetchMyProfile';
import { ScheduleCalendar } from './schedule/ScheduleCalendar';
import { AddRelationsModal } from '../components/RelatedProfiles/AddRelationsModal';
import { serverTimestamp } from 'firebase/firestore';
import { Actor, AppointmentActivityType, SourcePlatform } from '../Bridge';
import {
    makeFirestoreTTL_For6months,
    recordAppointmentActivityInFS,
} from '../utils/appointmentActivityRecorderInFS';
import { firestore } from '../firestore';

const WAIT_SINCE_TEXT: { [k: string]: string } = {
    CN: 'Cancelled',
    CND: 'Cancelled',
    CNS: 'Cancelled',
    RE: 'Rescheduled',
    RED: 'Rescheduled',
    RES: 'Rescheduled',
};

export const isCNRE = (status: string): boolean => {
    return WAIT_SINCE_TEXT[status] !== undefined;
};

export type A_ACTION_TYPE =
    | 'ONGOING'
    | 'START_VISIT'
    | 'ADD_PATIENT'
    | 'APPOINTMENT_DETAILS'
    | 'RE_START'
    | 'PARKED_ON_OTHER_DEVICE'
    | 'PARKED'
    | 'ABORTED';

export const ONGOING = 'ONGOING';
export const PARKED = 'PARKED';
export const PARKED_ON_OTHER_DEVICE = 'PARKED_ON_OTHER_DEVICE';
export const RE_START = 'RE_START';
export const START_VISIT = 'START_VISIT';
export const ADD_PATIENT = 'ADD_PATIENT';
export const APPOINTMENT_DETAILS = 'APPOINTMENT_DETAILS';
export const ABORTED = 'ABORTED';

export const openTeleWindow = (url: string): Window | null => {
    try {
        const width = 1053; // reason being in case of virtual BG it doesent render live preview on pre call screen if window is too small
        const height = 950;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;

        return window.open(
            url,
            'Eka care - Video call',
            `width=${width},height=${height},top=${top},left=${left}`,
        );
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getAppointmentActionTextForAction = (
    t: A_ACTION_TYPE,
    avoidCTAStyling?: boolean,
): React.ReactNode => {
    switch (t) {
        case ADD_PATIENT:
            return (
                <Fragment>
                    {avoidCTAStyling ? (
                        'Add Patient'
                    ) : (
                        <AntTooltip
                            placement="left"
                            content={'Add Gender before starting the visit'}
                        >
                            <div className="flex items-center space-x-4">
                                {renderCTAWithText('Add Patient')}
                            </div>
                        </AntTooltip>
                    )}
                </Fragment>
            );
        case ABORTED:
        case APPOINTMENT_DETAILS:
            return avoidCTAStyling ? (
                'Appt Details'
            ) : (
                <Fragment>
                    <div className="hidden xl:flex items-center justify-center w-16 h-16">
                        <svg
                            className="w-14 h-14 fill-actionPrimary"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                        >
                            <path d="M152 64H296V24C296 10.75 306.7 0 320 0C333.3 0 344 10.75 344 24V64H384C419.3 64 448 92.65 448 128V448C448 483.3 419.3 512 384 512H64C28.65 512 0 483.3 0 448V128C0 92.65 28.65 64 64 64H104V24C104 10.75 114.7 0 128 0C141.3 0 152 10.75 152 24V64zM48 448C48 456.8 55.16 464 64 464H384C392.8 464 400 456.8 400 448V192H48V448z"></path>
                        </svg>
                    </div>
                    <p className="font-700 text-actionPrimary text-13">Appt Details</p>
                </Fragment>
            );
        case ONGOING:
        case PARKED:
            return avoidCTAStyling ? 'Resume Visit' : renderCTAWithText('Resume Visit');
        case START_VISIT:
            return avoidCTAStyling ? 'Start Visit' : renderCTAWithText('Start Visit');

        case PARKED_ON_OTHER_DEVICE:
        case RE_START:
            return (
                <Fragment>
                    {avoidCTAStyling ? 'Resume Visit' : renderCTAWithText('Resume Visit')}
                    <p
                        className={`self-start w-4 h-4 min-w-4 min-h-4 rounded-full ${
                            avoidCTAStyling ? 'bg-white' : 'bg-actionPrimary'
                        } animate-pulse`}
                    ></p>
                    {!avoidCTAStyling && (
                        <div className="tooltip-tip left">
                            This visit might have updates from your staff.
                        </div>
                    )}
                </Fragment>
            );

        default:
            return '';
    }
};

const renderCTAWithText = (text: 'Start Visit' | 'Resume Visit' | string) => {
    if (text === 'Resume Visit') {
        return (
            <Fragment>
                <div className="hidden xl:flex items-center justify-center w-16 h-16">
                    <svg
                        className="w-16 h-16 fill-textColor-secondary"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 640 512"
                    >
                        <path d="M543.3 214.5C543.5 212.3 544 210.2 544 208C544 146.1 493.9 96 432 96c-11.41 0-22.19 2.207-32.56 5.377C368.9 59.51 319.8 32 264 32C170.8 32 95.41 107.9 96.08 200.8C40.24 220.6 0 273.4 0 336C0 415.5 64.47 480 144 480h360c75.11 0 136-60.89 136-136C640 282.7 599.1 231.4 543.3 214.5zM504 432H144c-52.94 0-96-43.07-96-96c0-40.2 25.77-76.36 64.13-89.97l32.19-11.42c-.2734-38.62-.2246-33.15-.293-37.17C145.4 132.4 198.7 80 264 80c38.4 0 73.63 18.1 96.66 49.67l20.14 27.6l32.67-9.988c48.86-14.94 84.23 25.56 81.96 64.4l-2.193 37.72l36.15 10.99C566.8 271.8 592 305.4 592 344C592 392.5 552.5 432 504 432zM336.1 175c-2.211-2.211-4.863-3.961-7.797-5.178c-5.867-2.43-12.48-2.43-18.34 0C307.9 171.1 305.2 172.8 303 175l-88 88c-9.375 9.375-9.375 24.56 0 33.94s24.56 9.375 33.94 0L296 249.9V376c0 13.25 10.75 24 24 24s24-10.75 24-24V249.9l47.03 47.03C395.7 301.7 401.8 304 408 304s12.28-2.344 16.97-7.031c9.375-9.375 9.375-24.56 0-33.94L336.1 175z"></path>
                    </svg>
                </div>
                <p className="font-700 text-actionPrimary text-13 whitespace-nowrap">{text}</p>
            </Fragment>
        );
    } else {
        return (
            <Fragment>
                <div className="hidden xl:flex items-center justify-center w-16 h-16">
                    <svg
                        className="w-14 h-14 fill-actionPrimary"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                    >
                        <path d="M416 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c17.67 0 32 14.33 32 32v256c0 17.67-14.33 32-32 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c53.02 0 96-42.98 96-96V128C512 74.98 469 32 416 32zM342.6 233.4l-128-128c-12.51-12.51-32.76-12.49-45.25 0c-12.5 12.5-12.5 32.75 0 45.25L242.8 224H32C14.31 224 0 238.3 0 256s14.31 32 32 32h210.8l-73.38 73.38c-12.5 12.5-12.5 32.75 0 45.25s32.75 12.5 45.25 0l128-128C355.1 266.1 355.1 245.9 342.6 233.4z"></path>
                    </svg>
                </div>
                <p className="font-700 text-actionPrimary text-13">{text}</p>
            </Fragment>
        );
    }
};

export function getAppointmentAction(a: AP | APWithOptionalpatient): A_ACTION_TYPE {
    if (a.visit_type === 'FLW' && ['IN', 'RV', 'BK'].includes(a.status)) {
        return START_VISIT;
    }

    return a.status === 'BK' || a.status === 'CK' || a.status === 'OG' || a.status === 'PA'
        ? a.stage === 'ONGOING'
            ? a.isLocalPresnt
                ? ONGOING
                : RE_START
            : a.stage === 'PARKED'
            ? a.isLocalPresnt
                ? PARKED
                : PARKED_ON_OTHER_DEVICE
            : a.patient_oid
            ? START_VISIT
            : ADD_PATIENT
        : a.status === 'AB'
        ? ABORTED
        : APPOINTMENT_DETAILS;
}

export const onAddToQueue = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    p: Patient,
    cid: string,
    onSuccess?: () => void,
    setLoading?: (val: boolean) => void,
    doc?: MyDoc,
): void => {
    e.stopPropagation();
    const { dispatch } = getStore();

    setLoading && setLoading(true);
    WithBEPatient<RequestResponse>(p, (bePt) =>
        bookAppointment({
            docId: doc?.id || getDoc()?._id || '',
            channel: 'Walkin',
            ptOid: bePt.id,
            cId: cid,
            dateTime: new Date().toISOString(),
        }),
    )(dispatch as Dispatch<PPA>)
        .then((res) => {
            setLoading && setLoading(false);
            if (res?.success) {
                onSuccess && onSuccess();
                if (his.location.pathname === GO_TO_APPOINTMENTS(cid)) {
                    ekaDispatch({ type: REFRESH_APPT });
                } else {
                    his.replace(GO_TO_APPOINTMENTS(cid));
                }
            } else {
                alert(res?.error?.type || '[1] Failed to add to queue');
                captureErrorString('Failed to add patient: ' + JSON.stringify(res));
                track('ERROR', { on: 'onAddToQueue', error: JSON.stringify(res) });
            }
        })
        .catch((e) => {
            setLoading && setLoading(false);
            alert('[2] Failed to add to queue');
            captureError(e);
            track('ERROR', { on: 'onAddToQueue', error: (e as Error).message });
        });
};

const getCurrVisit = async (a: APWithOptionalpatient) => {
    if (a.non_emr && a.doc_dwr_link) {
        window.open(a.doc_dwr_link, '_blank');
        return;
    }

    const visit = await getVisitById(a.aid);

    if (!visit) {
        console.error('Visit not found');
        return;
    }

    return visit;
};

export const openPrescriptionPadForVisit = async (
    context:
        | Appointments
        | AllPatientsComponent
        | PatientDetailsComponent
        | NewPatientComponent
        | NewQueue
        | StartVisitBtn
        | PastVisitsRightPanel
        | ScheduleCalendar
        | AddRelationsModal,
    a: APWithOptionalpatient,
    onComplete?: (pxId: string) => void,
): Promise<Visit | undefined> => {
    const currVisit = await getCurrVisit(a);

    if (!currVisit) {
        return;
    }

    context.props.history.push({
        pathname: GO_TO_PRESCRIPTION_PAD(currVisit.prescriptionId, 'edit'),
    });

    onComplete && onComplete(currVisit.prescriptionId);

    return currVisit;
};

export const getPatientNameText = (patient: AppointmentPatient): string => {
    return [patient.fn, patient.mn, patient.ln].filter(Boolean).join(' ');
};

export const getPhoneNumber = (patient: AppointmentPatient): string | undefined => {
    const phoneNumber =
        patient.mobile && patient.mobile.length > 0
            ? patient.mobile.substr(patient.mobile.length - 10)
            : patient.mobile?.replace('+91', '');

    return phoneNumber;
};

export const GENDER_CODE_TO_NAME: { [k: string]: string } = {
    M: 'Male',
    F: 'Female',
    O: 'Others',
};

const processTeleUrlsOnAppointmentStart = (appointmentObject: Appointment) => {
    // mode —— tele || in_clinic
    const vcMeta = appointmentObject.vc_meta;
    if (vcMeta) {
        if (vcMeta.platform === 'others') {
            if (vcMeta.meet_link) {
                openTeleWindow(vcMeta.meet_link);
            }
        } else {
            if (vcMeta.host_link) {
                openTeleWindow(vcMeta.host_link);
            }
        }
    }
};

const startVisit = async (
    context:
        | Appointments
        | AllPatientsComponent
        | PatientDetailsComponent
        | NewPatientComponent
        | NewQueue
        | StartVisitBtn
        | ScheduleCalendar
        | AddRelationsModal,
    a: APWithOptionalpatient,
    cId: string,
    patient?: AppointmentPatient,
    onComplete?: (pxId: string) => void,
    avoidTriggerBEStartVisit?: boolean,
) => {
    if (!a.patient_oid) {
        track('ERROR', { on: 'startVisit', error: 'patient_oid not found' });
        alert("Couldn't start visit. Please try again later or contact support [ASPO].");
        return;
    }

    if (a.non_emr && a.doc_dwr_link) {
        window.open(a.doc_dwr_link, '_blank');
        return;
    }

    let oldPtObj: Patient | undefined = undefined;
    if (a.patient_oid) {
        oldPtObj = await getPatientByLocalId(a.patient_oid);
    }
    if (!patient && !oldPtObj) {
        captureErrorString('[2] Patient not found while start visit:' + a.aid);
        track('ERROR', { on: 'startVisit', error: '[2] Patient not found' });
        alert("Couldn't start visit. Please try again later or contact support [ASAP] [2].");
        return;
    }

    let age: Age | undefined;
    const dob = patient?.dob || oldPtObj?.personal.age?.dob;
    if (dob) {
        const t = calculateAge(dob);
        age = { value: t.age, month: t.month, dob: dob };
    }

    const onFailure = async () => {
        await openPrescriptionPadForVisit(context, a, onComplete);
    };

    const onSuccess = async (prescriptionId: string) => {
        const shouldOpenSA =
            a.sa_id && 'saAppointment' in context.state && !context.state.saAppointment;

        context.props.history.push({
            pathname: GO_TO_PRESCRIPTION_PAD(
                prescriptionId,
                'edit',
                shouldOpenSA ? 'patient-overview' : undefined,
            ),
        });

        if (shouldOpenSA) {
            track('OPEN_SA', {
                channel: 'APPOINTMENTS_START_VISIT',
                apptVisitType: a['visit-type'],
                apptChannel: a.channel,
            });
        }

        onComplete && onComplete(prescriptionId);

        // const newPatient: NewPatient = {
        //     localId: a.patient_oid || undefined,
        //     oid: a.patient_oid || undefined,
        //     name: getPatientNameText(patient),
        //     phoneNumber: getPhoneNumber(patient),
        //     gender: GENDER_CODE_TO_NAME[patient.gen || ''] || '',
        //     age,
        //     bloodgroup: a.patient_oid ? a.patient.bloodgroup || undefined : undefined,
        //     onApp: a.onApp,
        //     formData: oldPtObj?.formData,
        // };

        // const settings = await getItemFromMisc<GenieSettings>(MISC_KEYS.settings);

        // context.props.dispatch(
        //     addPatient({
        //         newPatient,
        //         sendWelcome: settings.sendWelcome,
        //         visitid: a.aid,
        //         oldPtObj,
        //     }),
        // );
    };

    const initVisitAction = initVisit(
        // localId === oid === BE pattient id === pid
        {
            id: a.patient_oid,
            localId: a.patient_oid,
            uuid: a.patient_uuid || patient?.uuid,
            name: patient ? getPatientNameText(patient) : oldPtObj ? getPtFullName(oldPtObj) : '',
            phCode: oldPtObj?.personal.phone?.c || '',
            phoneNumber: patient ? getPhoneNumber(patient) : oldPtObj?.personal.phone?.n || '',
            gender: patient
                ? GENDER_CODE_TO_NAME[patient?.gen || ''] || ''
                : oldPtObj?.personal.gender || '',
            age: age,
            onApp: oldPtObj ? oldPtObj?.personal.onApp : a.onApp,
            formData: oldPtObj?.formData?.length ? oldPtObj?.formData : a?.formData,
            bloodgroup: a.patient_oid ? a.patient?.bloodgroup || undefined : undefined,
            abha: Array.isArray(oldPtObj?.abhaV2)
                ? oldPtObj?.abhaV2?.find((abha) => abha.cId === a.clinic_id)
                : a.abha || oldPtObj?.abha,
            relations: oldPtObj?.relations,
        },
        a.doctor_oid,
        cId,
        a.aid,
        onSuccess,
        undefined,
        {
            visitType: a['visit-type'],
            saId: a.sa_id || undefined,
            autoCopy: getAppointmentAction(a) === START_VISIT,
            avoidTriggerBEStartVisit,
        },
        onFailure,
    );
    track('Start_Visit', { from: 'Appointments' });
    initVisitAction && context.props.dispatch(initVisitAction);
};

export const onAppointmentAction = async (
    context:
        | Appointments
        | AllPatientsComponent
        | PatientDetailsComponent
        | NewPatientComponent
        | NewQueue
        | StartVisitBtn
        | ScheduleCalendar
        | AddRelationsModal,
    a: APWithOptionalpatient,
    cId: string,
    onComplete?: (pxId: string) => void,
    dontAllowStartVisit?: boolean,
    isNotesMode?: boolean,
    opts?: { flagToOpenTeleWindow?: boolean },
): Promise<void> => {
    const { patient, aid } = a;
    const { flagToOpenTeleWindow } = opts || {};

    const actionType = getAppointmentAction(a);
    const actorProfile = getMe();

    const appointmentAcitivityMapGenerator = (type: AppointmentActivityType) => {
        return {
            event: AppointmentActivityType[type],
            actor_id: actorProfile!.user.oid!,
            client_id: SourcePlatform.WEB,
            actor_role: actorProfile!.user['is-d-s'] ? Actor.STAFF : Actor.DOCTOR,
            created_at: serverTimestamp(),
            expires_at: makeFirestoreTTL_For6months(),
            actor_name: actorProfile!.user!.fn,
        };
    };

    track('APPT_SELECT', {
        option: actionType,
        aid: a.aid,
        clinic_id: a.clinic_id,
        cId,
        ccId: cId,
    });

    if (flagToOpenTeleWindow && a.mode === 'tele') {
        processTeleUrlsOnAppointmentStart(a);
    }
    switch (actionType) {
        case ABORTED:
        case APPOINTMENT_DETAILS: {
            'onAppointmentViewDetials' in context && context.onAppointmentViewDetials(aid);
            return;
        }

        case PARKED: {
            await openPrescriptionPadForVisit(context, a, onComplete);
            context.props.dispatch(
                triggerStartVisitOnBE({
                    visitId: a.aid,
                    docId: a.doctor_oid || getDoc()?._id || '',
                    clinicId: a.clinic_id,
                    pid: a.patient_oid || undefined,
                    startedAt: new Date().toISOString(),
                }),
            );
            return;
        }
        case PARKED_ON_OTHER_DEVICE:
            startVisit(context, a, a.clinic_id || cId, patient, onComplete);
            context.props.dispatch(
                triggerStartVisitOnBE({
                    visitId: a.aid,
                    docId: a.doctor_oid || getDoc()?._id || '',
                    clinicId: a.clinic_id,
                    pid: a.patient_oid || undefined,
                    startedAt: new Date().toISOString(),
                }),
            );
            return;
        case ONGOING: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (firestore) {
                await recordAppointmentActivityInFS(
                    appointmentAcitivityMapGenerator(AppointmentActivityType.VISIT_RESUMED),
                    a.aid,
                );
            }

            await openPrescriptionPadForVisit(context, a, onComplete);
            return;
        }
        case RE_START:
            startVisit(context, a, a.clinic_id || cId, patient, onComplete);
            return;
        case START_VISIT: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (firestore) {
                await recordAppointmentActivityInFS(
                    appointmentAcitivityMapGenerator(AppointmentActivityType.VISIT_STARTED),
                    a.aid,
                );
            }

            context.props.dispatch(setIsNotesMode(isNotesMode));
            context.props.dispatch(setDontAllowStartVisit(dontAllowStartVisit));
            startVisit(context, a, a.clinic_id || cId, patient, onComplete, dontAllowStartVisit);
            return;
        }
        case ADD_PATIENT: {
            if (!patient) {
                captureErrorString('Patient not found while start visit:' + a.aid);
                track('ERROR', {
                    on: 'startVisit',
                    error: '[1] patient_oid not found',
                    aid: a.aid,
                });
                alert('Please try again later or contact support [ASPA].');
                return;
            }
            let age;
            if (patient.dob) {
                const sp = patient.dob.split('-');
                if (sp.length === 3) {
                    age = {
                        dob: {
                            dd: sp[2],
                            mm: sp[1],
                            yyyy: sp[0],
                        },
                    };
                }
            }

            const s: NewPatientLocationState = {
                oid: a.patient_oid || undefined,
                phoneNumber: getPhoneNumber(patient),
                name: a.patient_oid ? getPatientNameText(patient) : undefined,
                visitId: a.aid,
                age: a.patient_oid ? age : undefined,
                visitType: a['visit-type'],
            };

            context.props.history.push({
                pathname: GO_TO_ADD_NEW_PATIENT(a.clinic_id || cId),
                state: s,
            });

            return;
        }
    }
};
